import { useEffect } from "react";

// Locks page scroll while a modal/overlay is open. Uses the
// position:fixed/top:-scrollY trick because plain `overflow:hidden` on body
// doesn't prevent rubber-band scroll on iOS Safari. Ref-counted so several
// overlays can stack without fighting over the body style.
let scrollLockCount = 0;
let savedScrollY = 0;
let savedBodyStyle: {
  overflow: string;
  position: string;
  top: string;
  width: string;
  paddingRight: string;
} | null = null;

function lockBodyScroll(): void {
  if (scrollLockCount === 0 && typeof document !== "undefined") {
    savedScrollY = window.scrollY;
    savedBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      paddingRight: document.body.style.paddingRight,
    };
    // Reserve the space the scrollbar occupied so locking the body doesn't
    // widen the page and shift content sideways.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = "100%";
    if (scrollbarWidth > 0) {
      const current = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${current + scrollbarWidth}px`;
    }
  }
  scrollLockCount++;
}

function unlockBodyScroll(): void {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0 && savedBodyStyle && typeof document !== "undefined") {
    document.body.style.overflow = savedBodyStyle.overflow;
    document.body.style.position = savedBodyStyle.position;
    document.body.style.top = savedBodyStyle.top;
    document.body.style.width = savedBodyStyle.width;
    document.body.style.paddingRight = savedBodyStyle.paddingRight;
    window.scrollTo(0, savedScrollY);
    savedBodyStyle = null;
  }
}

/** Locks body scroll while `active` is true; restores it on unmount/close. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [active]);
}
